/**
 * SweepModeBar Component
 *
 * Horizontal bar displayed above NFT grid when sweep mode is active.
 * Contains slider for selecting NFT count, total cost display, and action buttons.
 */

import React, { useMemo } from 'react';
import { Slider } from '../../ui/components/Slider';
import { Button } from '../../ui/components/Button';
import { FeatherX, FeatherShoppingCart } from '@subframe/core';
import type { NFT } from '../../types';
import { calculateSweepTotals } from '../../hooks/useSweepPurchase';
import { is3DModel } from '../../utils/media';

interface SweepModeBarProps {
  /** Available listed NFTs (filtered and sorted) */
  nfts: NFT[];
  /** Current sweep count */
  sweepCount: number;
  /** Callback when sweep count changes */
  onSweepCountChange: (count: number) => void;
  /** Callback when user clicks Continue */
  onContinue: () => void;
  /** Callback when user clicks Cancel */
  onCancel: () => void;
}

export function SweepModeBar({
  nfts,
  sweepCount,
  onSweepCountChange,
  onContinue,
  onCancel,
}: SweepModeBarProps) {
  // Calculate max available (capped at 20)
  const maxAvailable = Math.min(nfts.length, 20);

  // Get selected NFTs based on count
  const selectedNfts = useMemo(() => {
    return nfts.slice(0, sweepCount);
  }, [nfts, sweepCount]);

  // Calculate totals for selected NFTs
  const totals = useMemo(() => {
    return calculateSweepTotals(selectedNfts);
  }, [selectedNfts]);

  // Handle slider value change
  const handleSliderChange = (value: number[]) => {
    const newValue = Math.max(1, Math.min(value[0], maxAvailable));
    onSweepCountChange(newValue);
  };

  // If no NFTs available for sweep
  if (maxAvailable === 0) {
    return (
      <div className="w-full flex flex-col gap-3 p-4 bg-neutral-50 rounded-lg border border-solid border-neutral-border mobile:p-3">
        <div className="flex items-center justify-between">
          <span className="text-body font-body text-subtext-color">
            No listed NFTs available to sweep
          </span>
          <Button
            variant="neutral-tertiary"
            size="small"
            icon={<FeatherX />}
            onClick={onCancel}
          >
            Close
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full flex flex-col gap-4 p-4 bg-neutral-50 rounded-lg border border-solid border-neutral-border mobile:p-3 mobile:gap-3">
      {/* Sweep label and slider */}
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2 whitespace-nowrap">
          <FeatherShoppingCart className="text-brand-600 h-5 w-5" />
          <span className="text-body-bold font-body-bold text-default-font">
            Sweep: {sweepCount} NFT{sweepCount !== 1 ? 's' : ''}
          </span>
        </div>
        <div className="flex-1 flex items-center gap-3 min-w-[120px]">
          <span className="text-caption font-caption text-subtext-color">1</span>
          <Slider
            value={[sweepCount]}
            onValueChange={handleSliderChange}
            min={1}
            max={maxAvailable}
            step={1}
            className="flex-1"
          />
          <span className="text-caption font-caption text-subtext-color">{maxAvailable}</span>
        </div>
      </div>

      {/* Thumbnails row - hidden on small screens */}
      <div className="hidden sm:flex items-center gap-2">
        <span className="text-caption font-caption text-subtext-color whitespace-nowrap">Selected:</span>
        <div className="flex -space-x-1 overflow-hidden isolate">
          {selectedNfts.map((nft, index) => (
            is3DModel(nft) ? (
              <div
                key={nft.id}
                className="w-7 h-7 rounded border-2 border-solid border-default-background flex-shrink-0 flex items-center justify-center bg-neutral-100"
                style={{ zIndex: 20 - index }}
              >
                <span style={{ fontSize: '12px' }}>🧊</span>
              </div>
            ) : (
              <img
                key={nft.id}
                src={nft.thumbnail_url || nft.image_url || '/placeholder-nft.png'}
                alt={nft.name || 'NFT'}
                className="w-7 h-7 rounded border-2 border-solid border-default-background object-cover flex-shrink-0"
                style={{ zIndex: 20 - index }}
              />
            )
          ))}
        </div>
      </div>

      {/* Total and action buttons */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex flex-col gap-0.5 sm:text-left text-center">
          <span className="text-heading-3 font-heading-3 text-default-font whitespace-nowrap">
            Total: {totals.total.toFixed(2)} XRP
          </span>
          {totals.fees > 0 && (
            <span className="text-caption font-caption text-subtext-color whitespace-nowrap">
              ({totals.subtotal.toFixed(2)} XRP + ~{totals.fees.toFixed(2)} XRP fees)
            </span>
          )}
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <Button variant="neutral-secondary" onClick={onCancel} className="sm:w-auto w-full">
            Cancel
          </Button>
          <Button
            variant="brand-primary"
            onClick={onContinue}
            disabled={sweepCount === 0}
            className="sm:w-auto w-full"
          >
            Continue to Buy
          </Button>
        </div>
      </div>
    </div>
  );
}
